import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill, ActionChip } from './pills';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { ConfirmDialog } from './ConfirmDialog';
import { useWriteFeedback } from './useWriteFeedback';
import { optClasses } from './optionColor';

// Mirrors TaskService.TASK_STAGES (server) — duplicated, not imported: client code must never pull in
// server modules (TaskService imports the Prisma client via ./db, which is Node-only and would break
// the browser bundle). Same convention TaskBoard.tsx already uses for its own STAGES constant.
const TASK_STAGES = ['backlog', 'in_progress', 'review'] as const;

export interface TaskDetail {
  id: string;
  title: string;
  description: string | null;
  status: string;
  dueDate: string | null;
  completedAt: string | null;
  ownerId: string | null;
  ownerName: string | null;
  subtaskCount?: number;
  related: { kind: 'company' | 'lead' | 'deal'; id: string; name: string | null } | null;
}

// #50 (cmdr/DevOps, 2026-08-23): same converged fix as CustomerDetails — `status` empties to `''`
// (not a guessed real stage) so the render must treat it as "not yet known" via `task.status ? ... :
// null`, not translate it directly; see the AttributePill usage below.
function emptyTask(id: string): TaskDetail {
  return { id, title: '', description: null, status: '', dueDate: null, completedAt: null, ownerId: null, ownerName: null, subtaskCount: 0, related: null };
}

/**
 * TaskDetailPanel — Tasks-complete-loop (cmdr/UXeng, 2026-08-23). ONE surface for create AND detail/
 * edit: `taskId === 'new'` renders the create form (title + optional due only); on successful create
 * the SAME component instance flips to the created row's detail/edit view — "create and detail cannot
 * drift apart" (UXeng), because there is only one render path, not two screens kept in sync by hand.
 *
 * There is no separate edit screen: status/assignee/due/title/notes are each independently editable
 * in place (AttributePill->sheet for status/assignee, native OS picker for due, inline text for
 * title/notes) — UXeng: "the detail IS the edit surface."
 *
 * 3-tier per UXeng: desktop (>=lg) = side panel beside the list (caller sizes/positions the wrapper);
 * tablet (md-lg) = full overlay fixed inset-0 z-40; phone (<md) = same overlay + an explicit back
 * affordance >=44px (tablet's icon-rail nav remains reachable without one; phone's does not).
 */
export const TaskDetailPanel: React.FC<{
  taskId: string | 'new';
  sheetShape: 'sheet' | 'popover';
  onClose: () => void;
  onCreated: (task: TaskDetail) => void;
  onUpdated: (task: TaskDetail) => void;
  onDeleted: (taskId: string) => void;
}> = ({ taskId, sheetShape, onClose, onCreated, onUpdated, onDeleted }) => {
  const { t } = useTranslation('common');
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(taskId === 'new' ? 'ready' : 'loading');
  const [createTitle, setCreateTitle] = useState('');
  const [createDue, setCreateDue] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  // A3 (design §4.3) — see the TicketDetailPanel twin. Task's create POST returns the created
  // detail directly (no follow-up GET), so create here has only two outcomes, not three.
  const feedback = useWriteFeedback();
  const [createError, setCreateError] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [assignees, setAssignees] = useState<{ id: string; name: string | null }[]>([]);
  const [editing, setEditing] = useState<{ field: 'status' | 'assignee'; anchor: HTMLElement } | null>(null);
  const hasLoadedRef = useRef(false);

  // #50 (cmdr/DevOps, 2026-08-23): the two invariants are in TENSION — nulling `task` on a taskId
  // change caused the detach; simply not nulling it caused Task A's content to stay READABLE under
  // Task B's id (a scrim only blocks clicks, not reading — doesn't satisfy "never shows"). Fix: reset
  // to `emptyTask(taskId)` synchronously (never null, so nothing structurally unmounts) — every field
  // then reads honestly absent, same as CustomerDetails' converged fix.
  useEffect(() => {
    if (taskId === 'new') return;
    // 🔴 root fix (cmdr/DevOps, 2026-08-25 — same defect found+fixed in InvoiceDetailPanel, applied
    // here on the identical pattern): right after create, the parent flips taskId from 'new' to the
    // real id on the SAME panel instance — this effect re-fires, but `task` here already holds fresh
    // data for that exact id. Re-fetching anyway forces a real `loadState:'loading'` window, and every
    // inline-edit click handler gates on `!isLoading` — a click landing in that window is SILENTLY
    // SWALLOWED. Skip the redundant round-trip when we already have this exact record.
    if (task && task.id === taskId) { hasLoadedRef.current = true; return; }
    setLoadState('loading');
    if (hasLoadedRef.current) setTask(emptyTask(taskId));
    fetch(`/api/tasks/${taskId}`).then((r) => r.ok ? r.json() : Promise.reject(r.status)).then((d: TaskDetail) => {
      setTask(d); setLoadState('ready'); hasLoadedRef.current = true;
    }).catch(() => setLoadState('error'));
  }, [taskId]);

  useEffect(() => {
    fetch('/api/tasks/assignees').then((r) => r.ok ? r.json() : []).then(setAssignees).catch(() => {});
  }, []);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = createTitle.trim();
    if (!title) return;
    const res = await fetch('/api/tasks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, dueDate: createDue || null }),
    }).catch(() => null);
    // D-T3: a failed create is a SAVE error, never the load-error string — the typed title stays.
    if (!res?.ok) { setCreateError(true); return; }
    setCreateError(false);
    const created: TaskDetail = await res.json();
    setTask(created);
    onCreated(created);
  };

  const patch = async (data: Partial<{ title: string; status: string; dueDate: string | null; ownerId: string | null; description: string | null }>): Promise<boolean> => {
    if (!task) return false;
    const res = await fetch(`/api/tasks/${task.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    const updated: TaskDetail = await res.json();
    setTask(updated);
    onUpdated(updated);
    return true;
  };

  const toggleComplete = async () => {
    if (!task) return;
    const path = task.completedAt ? 'uncomplete' : 'complete';
    const res = await fetch(`/api/tasks/${task.id}/${path}`, { method: 'POST' }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
    const r = await fetch(`/api/tasks/${task.id}`).catch(() => null);
    if (!r?.ok) {
      // The toggle LANDED; only the confirmation failed — re-check, never re-toggle (a re-tap
      // would now UNDO the completion it claims to retry).
      feedback.unconfirmed(t('writeStates.confirmFailed'), t('writeStates.recheck'), () => { void toggleRecheck(); });
      return;
    }
    feedback.clear();
    const refreshed: TaskDetail = await r.json();
    setTask(refreshed);
    onUpdated(refreshed);
  };
  const toggleRecheck = async () => {
    if (!task) return;
    const r = await fetch(`/api/tasks/${task.id}`).catch(() => null);
    if (!r?.ok) return;
    feedback.clear();
    const refreshed: TaskDetail = await r.json();
    setTask(refreshed);
    onUpdated(refreshed);
  };

  // ConfirmDialog, never window.confirm (P7 / D-T6). The message NAMES the task (D-T5 — on a board
  // of forty cards a dialog that names nothing cannot be verified before accepting) and states the
  // subtask blast radius with a real count; at zero subtasks the clause never renders (X-5).
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDelete = async () => {
    if (!task) return;
    setConfirmingDelete(false);
    const res = await fetch(`/api/tasks/${task.id}`, { method: 'DELETE' });
    if (!res.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
    onDeleted(task.id);
    onClose();
  };

  const stageOptions: ValueSheetOption[] = TASK_STAGES.map((s) => ({ value: s, label: t(`taskBoard.stages.${s}`) }));
  const assigneeOptions: ValueSheetOption[] = assignees.map((a) => ({ value: a.id, label: a.name ?? '—' }));
  const roseFg = optClasses('rose').fg;

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="task-detail-panel">
      <button data-testid="task-detail-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 min-h-[44px] text-sm text-primary font-semibold"
        onClick={onClose}>&larr; {t('taskBoard.back')}</button>

      <div className="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
        {taskId === 'new' && !task && (
          <form data-testid="task-create-form" onSubmit={submitCreate} className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
            <input autoFocus data-testid="task-create-title" value={createTitle} onChange={(e) => setCreateTitle(e.target.value)}
              placeholder={t('taskBoard.titlePlaceholder')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
            <label className="text-xs text-text-muted flex flex-col gap-1">
              {t('taskBoard.dueDate')}
              <input data-testid="task-create-due" type="date" value={createDue} onChange={(e) => setCreateDue(e.target.value)}
                className="h-10 px-3 rounded-lg border border-border-neutral text-sm w-fit" />
            </label>
            {createError && <div className="text-xs text-opt-rose" data-testid="task-create-error">{t('writeStates.createFailed')}</div>}
            <button type="submit" disabled={!createTitle.trim()} data-testid="task-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 self-start">
              {t('taskBoard.create')}
            </button>
          </form>
        )}

        {taskId !== 'new' && loadState === 'loading' && !task && <div className="p-6 text-center text-sm text-text-muted">…</div>}
        {loadState === 'error' && !task && <div className="p-6 text-center text-sm text-text-muted" data-testid="task-detail-error">{t('customers.detail.loadError')}</div>}

        {task && (
          <div data-testid="task-detail-content" aria-busy={loadState === 'loading'}>
            {/* #50: `task` is never stale-and-wrong (emptyTask on a taskId change, never null) — this
                wrapper no longer hides content, it's just marked busy. The one residual write-risk
                (title/notes inline-edit blurring mid-revalidation) is accepted scope for this panel;
                Complete/Undo and the pickers below always act on the CURRENT (correct) taskId. */}
            {loadState === 'loading' && (
              <div data-testid="task-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">{t('customers.detail.loading')}</div>
            )}
            {/* identity */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              {editingTitle ? (
                <input autoFocus data-testid="task-title-input" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => { setEditingTitle(false); if (titleDraft.trim() && titleDraft !== task.title) void patch({ title: titleDraft.trim() }).then((ok) => { if (!ok) setEditingTitle(true); }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingTitle(false); }}
                  className="w-full h-9 px-2 -mx-2 rounded-lg border border-primary text-lg font-bold" dir="auto" />
              ) : (
                <h2 data-testid="task-title" className={`text-lg font-bold text-text-strong truncate cursor-text ${task.completedAt ? 'line-through opacity-60' : ''}`}
                  onClick={() => { setTitleDraft(task.title); setEditingTitle(true); }}>{task.title || '—'}</h2>
              )}
            </div>

            {/* state: status, due, assignee */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {/* #50: status ONLY translated when genuinely known — `''` (the emptyTask shell) must
                    render as "not set", never as a guessed/mistranslated real stage name. */}
                <AttributePill value={task.status ? t(`taskBoard.stages.${task.status}`) : null} label={t('taskBoard.stage')}
                  emptyText={t('customers.detail.setField', { field: t('taskBoard.stage') })}
                  onOpen={(a) => setEditing({ field: 'status', anchor: a })} />
                <AttributePill value={task.ownerName} label={t('taskBoard.assignee')}
                  emptyText={t('customers.detail.setField', { field: t('taskBoard.assignee') })}
                  onOpen={(a) => setEditing({ field: 'assignee', anchor: a })} />
              </div>
              <label className="text-xs text-text-muted flex flex-col gap-1 w-fit">
                {t('taskBoard.dueDate')}
                <input data-testid="task-due-input" type="date" value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
                  onChange={(e) => void patch({ dueDate: e.target.value || null })}
                  className="h-9 px-2 rounded-lg border border-border-neutral text-sm" />
              </label>
              <ActionChip testId="task-detail-complete-chip" label={task.completedAt ? t('taskBoard.undo') : t('taskBoard.complete')} onAct={toggleComplete} />
            </div>

            {/* related */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1">{t('taskBoard.related')}</div>
              {task.related ? (
                <div data-testid="task-related" className="text-sm text-text-strong">{task.related.name ?? <span className="text-text-muted/70">—</span>}</div>
              ) : (
                <div data-testid="task-related" className="text-sm text-text-muted/70">{t('taskBoard.noRelated')}</div>
              )}
            </div>

            {/* notes */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('taskBoard.notes')}</div>
              {editingNotes ? (
                <textarea autoFocus data-testid="task-notes-input" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
                  onBlur={() => { setEditingNotes(false); if (notesDraft !== (task.description ?? '')) void patch({ description: notesDraft || null }).then((ok) => { if (!ok) setEditingNotes(true); }); }}
                  className="w-full min-h-[80px] p-2 rounded-lg border border-primary text-sm" dir="auto" />
              ) : (
                <div data-testid="task-notes" className="text-sm text-text-strong whitespace-pre-wrap cursor-text min-h-[24px]"
                  onClick={() => { setNotesDraft(task.description ?? ''); setEditingNotes(true); }}>
                  {task.description || <span className="text-text-muted/70">{t('taskBoard.notesPlaceholder')}</span>}
                </div>
              )}
            </div>

            {/* delete — separated, text-only, rose, never a filled button */}
            <div className="pt-2 mt-2 border-t border-border-neutral">
              <button type="button" data-testid="task-delete" onClick={() => setConfirmingDelete(true)}
                className={`text-sm font-semibold min-h-[44px] px-1 ${roseFg}`}>
                {t('taskBoard.delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmingDelete && task && (
        <ConfirmDialog destructive
          message={(task.subtaskCount ?? 0) > 0
            ? t('taskBoard.deleteConfirmNamedWithSubtasks', { title: task.title, count: task.subtaskCount })
            : t('taskBoard.deleteConfirmNamed', { title: task.title })}
          onConfirm={() => void handleDelete()}
          onClose={() => setConfirmingDelete(false)} />
      )}
      {editing?.field === 'status' && task && (
        <ValueSheet shape={sheetShape} title={`${t('taskBoard.stage')} — ${task.title}`} options={stageOptions} current={task.status}
          anchor={editing.anchor} onSelect={(v) => { setEditing(null); if (v) void patch({ status: v }); }} onClose={() => setEditing(null)} />
      )}
      {editing?.field === 'assignee' && task && (
        <ValueSheet shape={sheetShape} title={`${t('taskBoard.assignee')} — ${task.title}`} options={assigneeOptions} current={task.ownerId}
          anchor={editing.anchor} onSelect={(v) => { setEditing(null); void patch({ ownerId: v }); }} onClose={() => setEditing(null)} />
      )}
      {feedback.node}
    </div>
  );
};
